import { useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTheme } from '@mui/material/styles';
import type { AppModule, ModuleKey } from './types';

interface AppSwitcherProps {
  modules: AppModule[];
  activeModule: ModuleKey;
  collapsed: boolean;
  onNavigate: (to: string) => void;
}

/**
 * Sits at the top of the sidebar and is the single most important piece of the
 * consolidation: it is what turns four separate applications into one product.
 *
 * Modules the user has no role in are shown but disabled, rather than hidden —
 * so people can see the portal covers them and know to ask for access, instead
 * of concluding the feature does not exist.
 */
export function AppSwitcher({ modules, activeModule, collapsed, onNavigate }: AppSwitcherProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const active = modules.find(m => m.key === activeModule);
  const ActiveIcon = active?.icon;

  const open = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const close = () => setAnchorEl(null);

  const select = (module: AppModule) => {
    close();
    if (module.available && module.key !== activeModule) onNavigate(module.to);
  };

  return (
    <>
      <Tooltip title={collapsed ? (active?.label ?? 'Switch module') : ''} placement="right">
        <ButtonBase
          onClick={open}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          aria-label={`Current module: ${active?.label ?? 'none'}. Switch module.`}
          sx={{
            width: '100%',
            gap: 1.25,
            px: collapsed ? 0 : 1.25,
            py: 1,
            borderRadius: `${theme.custom.radius.md}px`,
            justifyContent: collapsed ? 'center' : 'flex-start',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            transition: theme.transitions.create(['background-color', 'border-color']),
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: `${theme.custom.radius.sm}px`,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {ActiveIcon ? <ActiveIcon size={18} color="currentColor" /> : null}
          </Box>

          {!collapsed && (
            <>
              <Box sx={{ minWidth: 0, textAlign: 'start', flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  lineHeight={1.2}
                >
                  Module
                </Typography>
                <Typography variant="subtitle2" noWrap lineHeight={1.3}>
                  {active?.label ?? 'Select…'}
                </Typography>
              </Box>
              <UnfoldMoreIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
            </>
          )}
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        slotProps={{ paper: { sx: { minWidth: 260, mt: 0.5 } } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: 'block' }}
        >
          Modules
        </Typography>
        <Divider sx={{ mb: 0.5 }} />

        {modules.map(module => {
          const Icon = module.icon;
          const isActive = module.key === activeModule;
          return (
            <MenuItem
              key={module.key}
              selected={isActive}
              disabled={!module.available}
              onClick={() => select(module)}
              sx={{ py: 1, gap: 0.5 }}
            >
              <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'text.secondary' }}>
                <Icon size={20} color="currentColor" />
              </ListItemIcon>
              <ListItemText
                primary={module.label}
                secondary={module.available ? module.description : 'No access'}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 600 : 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              {!module.available && (
                <LockOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', ms: 1 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
